import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import type { Holding, BrokerAccount, PositionRule, RuleTemplate, RuleExecution, AggregatedHolding, AccountHolding } from '../types';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import EnhancedHoldingsTable from '../components/ui/EnhancedHoldingsTable';
import RuleCreationModal from '../components/ui/RuleCreationModal';
import RulesManagementInterface from '../components/ui/RulesManagementInterface';
import RuleTemplatesModal from '../components/ui/RuleTemplatesModal';
import CSVUploadWidget from '../components/ui/CSVUploadWidget';
import InlineHoldingForm from '../components/ui/InlineHoldingForm';
import { useAnalytics } from '../hooks/useAnalytics';

// Extended holding interface with calculated fields
interface ExtendedHolding extends Holding {
  purchase_value: number;
  day_change: number;
  day_change_percent: number;
  broker_type: string;
  broker_name: string;
}

type SortField = 'symbol' | 'total_quantity' | 'weighted_avg_price' | 'total_current_value' | 'total_pnl' | 'total_pnl_percent' | 'active_rules_count';
type SortDirection = 'asc' | 'desc';

// inline SVGs for eye / eye-off
const EyeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M1.5 12s4-7 10.5-7 10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12z" />
    <circle cx="12" cy="12" r="3" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const EyeOffIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M10.58 10.59A3 3 0 0112 9c1.657 0 3 1.343 3 3 0 .42-.086.82-.24 1.18m-.61 1.18A3.001 3.001 0 0112 15a3 3 0 01-3-3c0-.82.328-1.563.86-2.12M2.458 12C3.732 7.943 7.523 5 12 5c1.806 0 3.5.5 4.958 1.367M19.542 12c-.513 1.78-1.533 3.357-2.958 4.633" />
  </svg>
);

const Holdings: React.FC = () => {
  const { trackButtonClick, trackFormSubmission } = useAnalytics();
  
  const [holdings, setHoldings] = useState<ExtendedHolding[]>([]);
  const [brokerAccounts, setBrokerAccounts] = useState<BrokerAccount[]>([]);
  const [rules, setRules] = useState<PositionRule[]>([]);
  const [templates, setTemplates] = useState<RuleTemplate[]>([]);
  const [executions, setExecutions] = useState<RuleExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSensitiveData, setShowSensitiveData] = useState(false);
  const [sortField, setSortField] = useState<SortField>('symbol');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  // const [viewMode, setViewMode] = useState<'standard' | 'enhanced'>('enhanced');

  // Modal states
  const [showRuleCreationModal, setShowRuleCreationModal] = useState(false);
  const [showRulesManagementModal, setShowRulesManagementModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showCSVUploadModal, setShowCSVUploadModal] = useState(false);
  const [showInlineHoldingForm, setShowInlineHoldingForm] = useState(false);
  const inlineFormRef = useRef<HTMLDivElement>(null);
  const [showMagicSetupOverlay, setShowMagicSetupOverlay] = useState(false);
  const [selectedStockForModal, setSelectedStockForModal] = useState<{
    symbol: string;
    exchange: string;
    accounts: AccountHolding[];
    companyName?: string;
  } | null>(null);
  
  // Delete all holdings state
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);


  const [searchParams] = useSearchParams();
  const [autoExpandedSymbols] = useState<Set<string>>(new Set());
  const tableRef = useRef<any>(null);

  useEffect(() => {
    fetchData();
    fetchRuleData();
  }, []);

  // Check for CSV upload parameter and auto-open modal
  useEffect(() => {
    const uploadParam = searchParams.get('upload');
    if (uploadParam === 'csv') {
      setShowCSVUploadModal(true);
    }
  }, [searchParams]);



  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch holdings with enhanced data (includes current prices and broker info)
      const holdingsResponse = await apiService.holdings.getAll();
      const brokerAccountsResponse = await apiService.brokers.getAccounts();

      if (holdingsResponse.success) {
        // Data is already enhanced by the backend, minimal processing needed
        const enhancedHoldings = (holdingsResponse.data || []).map((holding: any) => ({
          ...holding,
          // Ensure numeric types
          quantity: typeof holding.quantity === 'string' ? parseFloat(holding.quantity) : holding.quantity,
          average_price: typeof holding.average_price === 'string' ? parseFloat(holding.average_price) : holding.average_price,
          current_price: typeof holding.current_price === 'string' ? parseFloat(holding.current_price) : holding.current_price,
          market_value: typeof holding.market_value === 'string' ? parseFloat(holding.market_value) : holding.market_value,
          pnl: typeof holding.pnl === 'string' ? parseFloat(holding.pnl) : holding.pnl,
          pnl_percent: typeof holding.pnl_percent === 'string' ? parseFloat(holding.pnl_percent) : holding.pnl_percent,
          purchase_value: typeof holding.purchase_value === 'string' ? parseFloat(holding.purchase_value) : holding.purchase_value,
          day_change: holding.day_change || 0,
          day_change_percent: holding.day_change_percent || 0,
        }));
        
        setHoldings(enhancedHoldings);
        
        // Set broker accounts for mapping
        if (brokerAccountsResponse.success) {
          setBrokerAccounts(brokerAccountsResponse.data || []);
        }
      } else {
        console.error('❌ Holdings request failed:', holdingsResponse);
        setError(`Failed to fetch holdings: ${holdingsResponse.error || 'Unknown error'}`);
      }

    } catch (err: any) {
      console.error('❌ Error fetching holdings:', err);
      
      // More detailed error message
      if (err.response) {
        setError(`Network error: ${err.response.status} - ${err.response.data?.error || 'Unknown error'}`);
      } else if (err.request) {
        setError('Network error: Unable to connect to server');
      } else {
        setError(`Error: ${err.message || 'Unknown error occurred'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRuleData = async () => {
    try {
      const [rulesRes, templatesRes] = await Promise.all([
        apiService.positionRules.list(),
        // Placeholder for future templates endpoint – keep hard-coded for now
        Promise.resolve({ success: true, data: [
          // SELL templates temporarily disabled - broker integration required
          // {
          //   id: 'tpl-10pct-trail',
          //   name: '10% Trailing Stop Loss',
          //   description: 'Sell all remaining shares when price drops 10% from peak',
          //   action_type: 'SELL' as const,
          //   trigger_type: 'TRAILING_STOP_LOSS' as const,
          //   trigger_value: 10,
          //   sell_quantity_type: 'ALL_REMAINING' as const,
          //   order_type: 'MARKET' as const
          // },
          {
            id: 'tpl-5pct-fixed',
            name: '5% Fixed Stop Loss',
            description: 'Alert when price falls 5% below purchase price',
            action_type: 'ALERT' as const,
            trigger_type: 'FIXED_STOP_LOSS' as const,
            trigger_value: 5
          }
          // SELL templates temporarily disabled - broker integration required
          // {
          //   id: 'tpl-15pct-book',
          //   name: '15% Profit Booking',
          //   description: 'Sell 50% when price rises 15% above purchase price',
          //   action_type: 'SELL' as const,
          //   trigger_type: 'TRAILING_STOP_LOSS' as const,
          //   trigger_value: 15,
          //   sell_quantity_type: 'PERCENTAGE' as const,
          //   sell_quantity_value: 50,
          //   order_type: 'MARKET' as const
          // }
        ]})
      ]);

      if (rulesRes.success) {
        setRules(rulesRes.data || []);
      }

      if (templatesRes.success) {
        setTemplates(templatesRes.data || []);
      }

      setExecutions([]); // Placeholder until executions endpoint exists
    } catch (err) {
      console.error('Error fetching rule data:', err);
    }
  };

  // Holdings without filtering (since filters section is removed)
  const filteredAndSortedHoldings = useMemo(() => {
    return holdings; // Return all holdings since we removed filtering
  }, [holdings]);

  // Calculate portfolio totals
  const portfolioSummary = useMemo(() => {
    const totalInvestment = filteredAndSortedHoldings.reduce((sum, holding) => {
      const purchaseValue = holding.purchase_value || (holding.quantity * holding.average_price);
      return sum + purchaseValue;
    }, 0);
    const totalCurrentValue = filteredAndSortedHoldings.reduce((sum, holding) => sum + (holding.market_value || 0), 0);
    const totalPnL = totalCurrentValue - totalInvestment;
    const totalPnLPercent = totalInvestment > 0 ? (totalPnL / totalInvestment) * 100 : 0;

    return {
      totalInvestment,
      totalCurrentValue,
      totalPnL,
      totalPnLPercent,
      totalHoldings: filteredAndSortedHoldings.length
    };
  }, [filteredAndSortedHoldings]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field as SortField);
      setSortDirection('asc');
    }
  };

  // Helper to render sort icons in table headers
  // const getSortIcon = (field: string) => {
  //   if (sortField !== field) {
  //     return (
  //       <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
  //         <path d="M6 8l4-4 4 4H6z" />
  //       </svg>
  //     );
  //   }
  //   return sortDirection === 'asc' ? (
  //     <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
  //         <path d="M6 12l4-4 4 4H6z" />
  //       </svg>
  //     ) : (
  //       <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
  //         <path d="M6 8l4 4 4-4H6z" />
  //       </svg>
  //     );
  // };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return '₹0.00';
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (percent: number | null | undefined) => {
    if (!showSensitiveData) return '****';
    if (percent === null || percent === undefined) return '0.00%';
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
  };

  // Modal handlers
  const handleDraftRuleSave = async (ruleDraft: Partial<PositionRule>) => {
    try {
      await apiService.positionRules.create(ruleDraft);
      await fetchRuleData();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to create rule';
      alert(msg);
      console.error('Rule creation failed', err);
    }
  };

  // const handleManageRules = (symbol: string, exchange: string, accounts: AccountHolding[], companyName?: string) => {
  //   setSelectedStockForModal({ symbol, exchange, accounts, companyName });
  //   setShowRulesManagementModal(true);
  // };

  const handleCreateRule = async (ruleData: Partial<PositionRule>) => {
    try {
      trackButtonClick('add_rule', '/holdings');
      trackFormSubmission('rule_creation', true);
      
      // TODO: Implement API call to create rule
      console.log('Creating rule:', ruleData);
      
      // Mock success for now
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Refresh rule data
      await fetchRuleData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      trackFormSubmission('rule_creation', false, errorMessage);
      console.error('Error creating rule:', error);
      throw error;
    }
  };

  const handleEditRule = (rule: PositionRule) => {
    // TODO: Implement rule editing
    console.log('Edit rule:', rule);
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      trackButtonClick('delete_rule', '/holdings');
      const res = await apiService.positionRules.delete(ruleId);
      if (res.success) {
        trackFormSubmission('rule_deletion', true);
        await fetchRuleData();
      } else {
        const errorMessage = typeof res.error === 'string' ? res.error : 'Failed to delete rule';
        trackFormSubmission('rule_deletion', false, errorMessage);
        alert(errorMessage);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete rule';
      trackFormSubmission('rule_deletion', false, msg);
      alert(msg);
      console.error('Error deleting rule:', err);
    }
  };

  const handleToggleRule = async (ruleId: string, isActive: boolean) => {
    try {
      trackButtonClick('toggle_rule', '/holdings');
      // TODO: Implement API call to toggle rule
      console.log('Toggling rule:', ruleId, isActive);
      
      // Mock success for now
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh rule data
      await fetchRuleData();
    } catch (error) {
      console.error('Error toggling rule:', error);
      throw error;
    }
  };

  const handleBulkToggle = async (ruleIds: string[], isActive: boolean) => {
    try {
      trackButtonClick('bulk_toggle_rules', '/holdings');
      // TODO: Implement API call to bulk toggle rules
      console.log('Bulk toggling rules:', ruleIds, isActive);
      
      // Mock success for now
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Refresh rule data
      await fetchRuleData();
    } catch (error) {
      console.error('Error bulk toggling rules:', error);
      throw error;
    }
  };

  const handleApplyTemplate = async (template: RuleTemplate, selectedHoldings: string[], accountScope: 'all' | 'specific', accountId?: number) => {
    try {
      trackButtonClick('apply_template', '/holdings');
      const rulesToCreate = selectedHoldings.map(symbol => ({
        ...template,
        symbol,
        exchange: 'NSE', // Default to NSE for templates
        rule_scope: accountScope,
        target_account_id: accountScope === 'specific' ? accountId : undefined,
        is_active: true,
        priority: 1
      }));

      await apiService.positionRules.bulkCreate(rulesToCreate);
      trackFormSubmission('template_application', true);
      await fetchRuleData();
      setShowTemplatesModal(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      trackFormSubmission('template_application', false, errorMessage);
      console.error('Error applying template:', error);
    }
  };

  const handleDeleteAllHoldings = async () => {
    if (!window.confirm('Are you sure you want to delete ALL holdings from ALL accounts? This action cannot be undone.')) return;
    setDeleteAllLoading(true);

    try {
      trackButtonClick('delete_all_holdings', '/holdings');
      const response = await apiService.holdings.deleteAll();
      if (response.success) {
        trackFormSubmission('delete_all_holdings', true);
        await fetchData(); // Refresh holdings
      } else {
        const errorMessage = typeof response.error === 'string' ? response.error : 'Failed to delete all holdings';
        trackFormSubmission('delete_all_holdings', false, errorMessage);
        alert('Failed to delete all holdings. Please try again.');
      }
    } catch (err: unknown) {
      let errorMsg = 'Failed to delete all holdings';
      if (err instanceof Error) {
        errorMsg = err.message;
      } else if (typeof err === 'string') {
        errorMsg = err;
      }
      trackFormSubmission('delete_all_holdings', false, errorMsg);
      alert(`Error: ${errorMsg}`);
    } finally {
      setDeleteAllLoading(false);
    }
  };

  const handleDeleteHolding = async (holdingId: string) => {
    if (!confirm('Are you sure you want to delete this holding? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiService.holdings.delete(holdingId);
      if (response.success) {
        await fetchData(); // Refresh holdings
      } else {
        const errorMessage = response.error || 'Failed to delete holding';
        setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to delete holding');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete holding';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to delete holding');
    }
  };

  // New function to handle deleting aggregated holdings (symbol+exchange combinations)
  const handleDeleteAggregatedHolding = async (symbol: string, exchange: string) => {
    // Find all individual holdings that match this symbol+exchange
    const matchingHoldings = holdings.filter(h => 
      h.symbol === symbol && h.exchange === exchange
    );

    if (matchingHoldings.length === 0) {
      setError('No holdings found to delete');
      return;
    }

    // If there's only one account, delete it directly
    if (matchingHoldings.length === 1) {
      await handleDeleteHolding(matchingHoldings[0].id);
      return;
    }

    // If there are multiple accounts, show a confirmation with details
    const accountDetails = matchingHoldings.map(h => 
      `${h.broker_name || 'Unknown'} (${h.broker_type || 'Unknown'}): ${h.quantity} shares`
    ).join('\n');

    const message = `You have ${symbol} (${exchange}) in ${matchingHoldings.length} accounts:\n\n${accountDetails}\n\nDo you want to delete ALL holdings for ${symbol}? This will remove it from all accounts and delete associated rules.`;
    
    if (!confirm(message)) {
      return;
    }

    try {
      // Delete all matching holdings
      const deletePromises = matchingHoldings.map(holding => 
        apiService.holdings.delete(holding.id)
      );

      const results = await Promise.all(deletePromises);
      const failedResults = results.filter(result => !result.success);
      
      if (failedResults.length > 0) {
        const errorMessages = failedResults.map(result => 
          result.error || 'Failed to delete holding'
        );
        setError(`Failed to delete some holdings:\n${errorMessages.join('\n')}`);
      } else {
        await fetchData(); // Refresh holdings
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete holdings';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to delete holdings');
    }
  };

  // Get aggregated holdings for enhanced view
  const aggregatedHoldings = useMemo(() => {
    const grouped = holdings.reduce((acc, holding) => {
      const key = `${holding.symbol}-${holding.exchange}`;
      
      if (!acc[key]) {
        acc[key] = {
          symbol: holding.symbol,
          exchange: holding.exchange,
          company_name: holding.company_name,
          total_quantity: 0,
          weighted_avg_price: 0,
          total_investment: 0,
          total_current_value: 0,
          total_pnl: 0,
          total_pnl_percent: 0,
          accounts: [],
          active_rules_count: 0
        };
      }

      const purchaseValue = holding.quantity * holding.average_price;
      const currentValue = holding.market_value || (holding.quantity * (holding.current_price || holding.average_price));
      const pnl = currentValue - purchaseValue;
      const pnlPercent = purchaseValue > 0 ? (pnl / purchaseValue) * 100 : 0;

      // Count only active rules for this stock+account combination (exclude triggered rules)
      const accountRulesCount = rules.filter(rule => 
        rule.symbol === holding.symbol && 
        rule.exchange === holding.exchange && 
        rule.is_active === true &&
        (rule.rule_scope === 'all_holdings' || rule.target_account_id === holding.broker_account_id)
      ).length;

      const accountHolding: AccountHolding = {
        id: holding.id, // Include holding ID for editing
        broker_account_id: holding.broker_account_id,
        broker_name: holding.broker_name || 'Unknown',
        broker_type: holding.broker_type || 'Unknown',
        quantity: holding.quantity,
        average_price: holding.average_price,
        current_price: holding.current_price,
        market_value: currentValue,
        pnl: pnl,
        pnl_percent: pnlPercent,
        purchase_value: purchaseValue,
        active_rules_count: accountRulesCount
      };

      acc[key].accounts.push(accountHolding);
      acc[key].total_quantity += holding.quantity;
      acc[key].total_investment += purchaseValue;
      acc[key].total_current_value += currentValue;
      acc[key].total_pnl += pnl;

      return acc;
    }, {} as Record<string, AggregatedHolding>);

    // Calculate weighted averages and totals
    Object.values(grouped).forEach(aggregated => {
      aggregated.weighted_avg_price = aggregated.total_investment / aggregated.total_quantity;
      aggregated.total_pnl_percent = aggregated.total_investment > 0 
        ? (aggregated.total_pnl / aggregated.total_investment) * 100 
        : 0;
      
      // Count total active rules for this symbol (exclude triggered rules)
      aggregated.active_rules_count = rules.filter(rule => 
        rule.symbol === aggregated.symbol && 
        rule.exchange === aggregated.exchange && 
        rule.is_active === true
      ).length;
    });

    // Sort holdings with rules on top, then by symbol
    const sortedHoldings = Object.values(grouped).sort((a, b) => {
      // First sort by active rules count (descending)
      if (a.active_rules_count !== b.active_rules_count) {
        return b.active_rules_count - a.active_rules_count;
      }
      // Then sort by symbol alphabetically
      return a.symbol.localeCompare(b.symbol);
    });

    return sortedHoldings;
  }, [holdings, rules]);

  // Handle magic-setup parameter and show overlay
  useEffect(() => {
    const magicSetupParam = searchParams.get('magic-setup');
    if (magicSetupParam === 'true') {
      // Clear the parameter from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('magic-setup');
      window.history.replaceState({}, '', `${window.location.pathname}?${newSearchParams.toString()}`);
      
      // Show magic setup overlay after data loads
      setTimeout(() => {
        // Only show overlay when data is loaded and we have holdings
        if (aggregatedHoldings.length > 0 && !loading) {
          setShowMagicSetupOverlay(true);
        }
      }, 500);
    }
  }, [searchParams, aggregatedHoldings, loading]);

  // Handle magic setup button click
  const handleMagicSetupClick = () => {
    trackButtonClick('magic_setup', '/holdings');
    setShowMagicSetupOverlay(false);
    // Trigger magic setup after overlay disappears
    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.triggerMagicSetup();
      }
    }, 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      <div className="max-w-7xl mx-auto p-3 sm:p-4 lg:p-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6 lg:mb-8 animate-fade-in">
                      <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-1 sm:mb-2">Portfolio Holdings</h1>
              </div>
            </div>
        </div>

        {/* Portfolio Summary Table */}
        <div className="card bg-white border border-gray-200 mb-4 sm:mb-6 lg:mb-8 animate-slide-in">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Portfolio Summary</h3>
            <button
              onClick={() => setShowSensitiveData((prev: boolean) => !prev)}
              className="text-gray-600 hover:text-gray-800 focus:outline-none p-1 rounded-md hover:bg-gray-100 transition-colors"
              title={showSensitiveData ? 'Hide sensitive data' : 'Show sensitive data'}
            >
              {showSensitiveData ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-sm font-medium text-gray-600 text-left w-1/4">Total Investment</th>
                  <th className="px-4 py-3 text-sm font-medium text-gray-600 text-left w-1/4">Current Value</th>
                  <th className="px-4 py-3 text-sm font-medium text-gray-600 text-left w-1/4">P&L</th>
                  <th className="px-4 py-3 text-sm font-medium text-gray-600 text-left w-1/4">Holdings Count</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-600 font-normal">
                    <span className="inline-block min-w-[120px]">
                      {showSensitiveData ? formatCurrency(portfolioSummary.totalInvestment) : '****'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-normal">
                    <span className="inline-block min-w-[120px]">
                      {showSensitiveData ? formatCurrency(portfolioSummary.totalCurrentValue) : '****'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-normal">
                    <span className={`${portfolioSummary.totalPnL >= 0 ? 'text-green-700' : 'text-red-800'}`}>
                      {showSensitiveData 
                        ? `${formatCurrency(portfolioSummary.totalPnL)} (${formatPercent(portfolioSummary.totalPnLPercent)})`
                        : '**** (****)'
                      }
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-normal">
                    <span className="inline-block min-w-[80px]">
                      {portfolioSummary.totalHoldings}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>



        {error && (
          <div className="card bg-gradient-to-r from-red-50 to-rose-50 border-red-200 p-4 mb-6 animate-fade-in">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}

      {/* Holdings Table */}
      <EnhancedHoldingsTable
        ref={tableRef}
        holdings={aggregatedHoldings}
        brokerAccounts={brokerAccounts}
        rules={rules}
        onAddRule={handleDraftRuleSave}
        onBulkSaved={fetchData}
        onDeleteRule={handleDeleteRule}
        onDeleteAggregatedHolding={handleDeleteAggregatedHolding}
        searchTerm=""
        selectedAccount="all"
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        autoExpandedSymbols={autoExpandedSymbols}
        onAddNew={() => {
          setShowInlineHoldingForm(true);
          // Scroll to the form after a brief delay to ensure it's rendered
          setTimeout(() => {
            if (inlineFormRef.current) {
              inlineFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }}
        inlineFormSlot={showInlineHoldingForm ? (
          <div ref={inlineFormRef} className="mb-4">
            <InlineHoldingForm
              onSuccess={() => {
                fetchData();
                setShowInlineHoldingForm(false);
              }}
              onCancel={() => {
                setShowInlineHoldingForm(false);
              }}
              initialRows={1}
              showPreFilledExample={false}
              showUploadButton={false}
              showAddButton={false}
            />
          </div>
        ) : null}
      />

      {/* Upload CSV and Delete All Actions */}
      <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <button
          onClick={() => {
            trackButtonClick('upload_csv', '/holdings');
            setShowCSVUploadModal(true);
          }}
          className="btn btn-success px-4 py-2 shadow-md hover:shadow-lg group text-sm"
        >
          <svg className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          Upload CSV
        </button>
        
        {aggregatedHoldings.length > 0 && (
          <button
            className="btn btn-danger px-4 py-2 shadow-md hover:shadow-lg group text-sm disabled:opacity-50"
            onClick={handleDeleteAllHoldings}
            disabled={deleteAllLoading}
          >
            {deleteAllLoading ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
                Deleting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete All Holdings
              </>
            )}
          </button>
        )}
      </div>

      {/* Modals */}
      {showCSVUploadModal && (
        <CSVUploadWidget
          onUploadSuccess={fetchData}
          onClose={() => setShowCSVUploadModal(false)}
        />
      )}
      
      {showRuleCreationModal && selectedStockForModal && (
        <RuleCreationModal
          isOpen={showRuleCreationModal}
          symbol={selectedStockForModal.symbol}
          exchange={selectedStockForModal.exchange}
          accounts={selectedStockForModal.accounts}
          templates={templates}
          companyName={selectedStockForModal.companyName}
          onClose={() => {
            setShowRuleCreationModal(false);
            setSelectedStockForModal(null);
          }}
          onSubmit={handleCreateRule}
        />
      )}

      {showRulesManagementModal && selectedStockForModal && (
        <RulesManagementInterface
          isOpen={showRulesManagementModal}
          symbol={selectedStockForModal.symbol}
          exchange={selectedStockForModal.exchange}
          accounts={selectedStockForModal.accounts}
          companyName={selectedStockForModal.companyName}
          rules={rules.filter(rule => 
            rule.symbol === selectedStockForModal.symbol && 
            rule.exchange === selectedStockForModal.exchange
          )}
          executions={executions}
          onClose={() => {
            setShowRulesManagementModal(false);
            setSelectedStockForModal(null);
          }}
          onEditRule={handleEditRule}
          onDeleteRule={handleDeleteRule}
          onToggleRule={handleToggleRule}
          onBulkToggle={handleBulkToggle}
        />
      )}

      {showTemplatesModal && (
        <RuleTemplatesModal
          isOpen={showTemplatesModal}
          templates={templates}
          holdings={aggregatedHoldings}
          onClose={() => setShowTemplatesModal(false)}
          onApplyTemplate={handleApplyTemplate}
        />
      )}

      {/* Magic Setup Overlay */}
      {showMagicSetupOverlay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#441d91' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Price Guard - Smart Protection</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Price Guard quickly adds downside protection to your entire portfolio in seconds. These smart alerts notify you when your investment changes direction, allowing you exit in time to avoid significant losses.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setShowMagicSetupOverlay(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Skip for Now
              </button>
              <button
                onClick={handleMagicSetupClick}
                className="px-6 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#ba9df5' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#371778';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#9b88c2';
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Price Guard
              </button>
            </div>
            
            <div className="mt-4 text-xs text-gray-500">
              You can always customize these rules later
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Holdings; 